import type { FC } from 'react';
import { Typography, Checkbox, FormControlLabel, Tooltip, Chip } from '@mui/material';
import ButtonGlynt from '../common/Buttons/ButtonGlynt';
import { btnSubtleNeutralSx } from '../common/filterStyles';
import { withDiscoveryPosition } from './matDiscoveryTableHelpers';

interface MATDiscoveryTableHeaderProps {
  current: any;
  discoveredOnly: boolean;
  reviewedOnly: boolean;
  isDiscoveryLoading: boolean;
  index: number;
  total: number;
  hasNext: boolean;
  blockEditing: boolean;
  onNext: () => void;
  onPrevious: () => void;
  searchParams: URLSearchParams;
  navigate: any;
}

const MATDiscoveryTableHeader: FC<MATDiscoveryTableHeaderProps> = ({
  current,
  discoveredOnly,
  reviewedOnly,
  isDiscoveryLoading,
  index,
  total,
  hasNext,
  blockEditing,
  onNext,
  onPrevious,
  searchParams,
  navigate,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
    }}
  >
    <div>
      {current ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#222' }}>
            MAT Key:
            <span
              style={{
                fontWeight: 900,
                fontSize: '1.15em',
                marginLeft: 6,
                letterSpacing: '0.5px',
              }}
            >
              {current.mat_key ?? ''}
            </span>
          </Typography>
          {current.data_source_status && (
            <Chip
              label={current.data_source_status}
              color={
                current.data_source_status === 'discovered'
                  ? 'primary'
                  : current.data_source_status === 'active'
                    ? 'success'
                    : current.data_source_status === 'inactive'
                      ? 'default'
                      : 'secondary'
              }
              size="small"
            />
          )}
        </div>
      ) : null}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {/* Filters: Discovered Only, Reviewed */}
      <Tooltip title="Only show new mat keys that don't exist in the system">
        <FormControlLabel
          control={<Checkbox size="small" color="primary" checked={discoveredOnly} />}
          onChange={(_, checked) => {
            const params = withDiscoveryPosition(searchParams, { index: 0 });
            if (checked) {
              params.set('has_existing_mat', 'false');
            } else {
              params.delete('has_existing_mat');
            }
            navigate({ search: params.toString() }, { replace: true });
          }}
          label="Discovered Only"
          sx={{ mr: 1 }}
        />
      </Tooltip>
      <Tooltip title="Only show data from documents that have been reviewed">
        <FormControlLabel
          control={<Checkbox size="small" color="primary" checked={reviewedOnly} />}
          onChange={(_, checked) => {
            const params = withDiscoveryPosition(searchParams, { index: 0 });
            if (checked) {
              params.set('data_verified', 'true');
            } else {
              params.delete('data_verified');
            }
            navigate({ search: params.toString() }, { replace: true });
          }}
          label="Reviewed"
          sx={{ mr: 2 }}
        />
      </Tooltip>
      <Typography variant="body2" sx={{ color: '#555' }}>
        {isDiscoveryLoading ? 'Entry ... of ...' : `Entry ${index + 1} of ${total}`}
      </Typography>
      <ButtonGlynt
        variant="outlined"
        sx={{ ...btnSubtleNeutralSx, mr: 1 }}
        onClick={onPrevious}
        disabled={index <= 0 || blockEditing}
      >
        Prev
      </ButtonGlynt>
      <ButtonGlynt
        variant="outlined"
        sx={btnSubtleNeutralSx}
        onClick={onNext}
        disabled={!hasNext || blockEditing}
      >
        Next
      </ButtonGlynt>
    </div>
  </div>
);

export default MATDiscoveryTableHeader;
